import { dashboardConfig } from 'config/routeConfigurations/dashboardConfig/dashboardConfig';
import colorsVars from 'styles/colors.module.scss';
import { formatValueForGraph, roundNumber } from 'utilitys/helpers/general';

import { findClosestNumber } from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/ClusterRCFAnalysis.utils';
import { riskConfig } from 'components/Dashboards/ExcellenceApps/riskConfig';
import { FormProps } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/CreateRiskRegisterModal/components/CreateRiskRegisterForm/CreateRiskRegisterForm';

const { white, cardGraphFont, black, pointColorForGraph } = colorsVars;

interface SerializeDataProps {
    data: { percentile: number; value: number }[];
    themeStatus: boolean;
    yaxisName: string;
}

export const serializeData = ({
    data,
    themeStatus,
    yaxisName,
}: SerializeDataProps): ApexAxisChartSeries | ApexNonAxisChartSeries | undefined => {
    return [
        {
            name: yaxisName,
            data: data.map((item) => ({
                y: item.value,
                x: item.percentile,
                fillColor: pointColorForGraph,
                strokeColor: pointColorForGraph,
                width: 5,
            })),
            color: themeStatus ? white : cardGraphFont,
        },
    ];
};

interface SerializeOptionsProps {
    data: { percentile: number; value: number }[];
    themeStatus: boolean;
    yaxisName: string;
    isAnnotation: boolean;
}

export const serializeOptions = ({
    data,
    themeStatus,
    yaxisName,
    isAnnotation,
}: SerializeOptionsProps): ApexCharts.ApexOptions => {
    const categories = data.map((item) => item.percentile);
    const tooltipLabels = data.map((item) => `${roundNumber(item.percentile, 2)}, ${roundNumber(item.value, 2)}`);

    return {
        grid: {
            xaxis: {
                lines: {
                    show: true,
                },
            },
        },
        chart: {
            background: themeStatus ? black : white,
            foreColor: cardGraphFont,
            toolbar: {
                show: false,
            },
            zoom: {
                enabled: false,
            },
        },
        xaxis: {
            type: 'numeric',
            categories: categories,
            min: 0,
            max: 100,
            tickAmount: 3,
            labels: {
                formatter: (value) => {
                    return formatValueForGraph(Number(value));
                },
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                },
            },
            title: {
                text: 'Percentile',
                offsetX: 0,
                offsetY: 0,
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                },
            },
        },
        stroke: {
            width: 3,
        },
        yaxis: {
            min: 0,
            tickAmount: 6,
            labels: {
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '16px',
                },
                formatter: (value) => {
                    return formatValueForGraph(value);
                },
            },
            title: {
                text: yaxisName,
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '16px',
                    fontWeight: 400,
                },
            },
        },
        markers: {
            size: 7,
        },
        tooltip: {
            theme: themeStatus ? 'dark' : 'light',
            style: {
                fontFamily: 'Roboto',
                fontSize: '14px',
            },
            y: {
                formatter: function (value, { dataPointIndex }) {
                    return tooltipLabels[dataPointIndex];
                },
            },
        },
        annotations: {
            yaxis: isAnnotation
                ? [
                      {
                          y: 1,
                          strokeDashArray: 4,
                          borderColor: themeStatus ? white : black,
                          label: {
                              position: 'center',
                              borderColor: 'transparent',
                              offsetY: 30,
                              offsetX: 130,
                              style: {
                                  color: themeStatus ? white : black,
                                  fontWeight: 'bold',
                                  background: themeStatus ? black : white,
                                  fontFamily: 'Roboto',
                                  fontSize: '14px',
                              },
                              text: 'On Time',
                          },
                      },
                  ]
                : [],
        },
    };
};

export const generateLinkToCreateRisk = ({
    id,
    selectedCluster,
    durationOrDurationRatio,
    data,
}): { pathname: string; state: Partial<FormProps> } => {
    const multiply = durationOrDurationRatio.value === 'Duration' ? 1 : 100;

    return {
        pathname: `/dashboard/${dashboardConfig.risk.link}/${riskConfig.riskRegister.link}/${id}/create`,
        state: {
            name: `Risk per cluster '${selectedCluster?.clusterName}'`,
            probability: 'CERTAIN',
            impactType: durationOrDurationRatio.value === 'Duration' ? 'ABSOLUTE' : 'RELATIVE',
            distribution: 'TRIANGLE',
            distributionRange: {
                min: getDistributionRangeValues(data, 25, multiply),
                middle: getDistributionRangeValues(data, 50, multiply),
                max: getDistributionRangeValues(data, 75, multiply),
            },
            description: `Risk created based on duration distribution of RCF cluster '${selectedCluster?.clusterName}'.`,
            activitiesByKey: {
                cluster: (selectedCluster?.clusterId as number) ?? null,
            },
            linkOption: 'cluster',
        },
    };
};

const getDistributionRangeValues = (data, target, multiply) => {
    const item = getValueFromData({ data, target });
    return item ? Number((item.value * multiply).toFixed(0)) : 0;
};

interface GetValueFromDataProps {
    data: { percentile: number; value: number }[] | null;
    target: number;
}
export const getValueFromData = ({
    data,
    target,
}: GetValueFromDataProps): { percentile: number; value: number } | undefined => {
    return data?.find(
        (item) => item.percentile === findClosestNumber({ data, target, key: 'percentile', initial: target }),
    );
};
